import { useEffect } from 'react';
import { SITE_ORIGIN } from '../../config/site';
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/apple-touch-icon.png`;

const JSON_LD_SCRIPT_ATTR = 'data-qinvesting-document-meta-jsonld';

type DocumentMetaProps = {
  title: string;
  description: string;
  /** Path only, e.g. `/stock/AAPL`. Omit or null to skip canonical. */
  canonicalPath?: string | null;
  ogType?: string;
  /** When true, sets robots noindex (and skips canonical). */
  robotsNoIndex?: boolean;
  /** Injected as a single `application/ld+json` script; removed on unmount. */
  jsonLd?: unknown[] | null;
};

function escapeJsonLdText(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function mountHomeJsonLdScript(blocks: unknown[]): HTMLScriptElement {
  document.head
    .querySelectorAll(`script[${JSON_LD_SCRIPT_ATTR}]`)
    .forEach((node) => node.remove());
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(JSON_LD_SCRIPT_ATTR, 'true');
  script.text = escapeJsonLdText(blocks);
  document.head.appendChild(script);
  return script;
}

type HeadTagSnapshot = {
  element: HTMLElement;
  created: boolean;
  previousContent: string | null;
  previousHref: string | null;
};

function snapshotMeta(
  attribute: 'name' | 'property',
  key: string,
  content: string,
): HeadTagSnapshot {
  const selector = `meta[${attribute}="${key}"]`;
  const existing = document.head.querySelector(selector) as HTMLMetaElement | null;
  if (existing) {
    const previousContent = existing.getAttribute('content');
    existing.setAttribute('content', content);
    return { element: existing, created: false, previousContent, previousHref: null };
  }
  const element = document.createElement('meta');
  element.setAttribute(attribute, key);
  element.setAttribute('content', content);
  document.head.appendChild(element);
  return { element, created: true, previousContent: null, previousHref: null };
}

function snapshotCanonical(href: string): HeadTagSnapshot {
  const existing = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (existing) {
    const previousHref = existing.getAttribute('href');
    existing.setAttribute('href', href);
    return { element: existing, created: false, previousContent: null, previousHref };
  }
  const element = document.createElement('link');
  element.setAttribute('rel', 'canonical');
  element.setAttribute('href', href);
  document.head.appendChild(element);
  return { element, created: true, previousContent: null, previousHref: null };
}

function detachCanonical(): HeadTagSnapshot | null {
  const existing = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!existing) return null;
  const snapshot: HeadTagSnapshot = {
    element: existing,
    created: false,
    previousContent: null,
    previousHref: existing.getAttribute('href'),
  };
  existing.remove();
  return snapshot;
}

function restoreHeadTag(snapshot: HeadTagSnapshot): void {
  if (snapshot.created) {
    snapshot.element.remove();
    return;
  }
  if (snapshot.element instanceof HTMLLinkElement) {
    if (snapshot.previousHref != null) {
      snapshot.element.setAttribute('href', snapshot.previousHref);
    } else {
      snapshot.element.removeAttribute('href');
    }
    return;
  }
  if (snapshot.previousContent != null) {
    snapshot.element.setAttribute('content', snapshot.previousContent);
  } else {
    snapshot.element.removeAttribute('content');
  }
}

function restoreDetachedCanonical(snapshot: HeadTagSnapshot | null): void {
  if (!snapshot) return;
  if (snapshot.previousHref) {
    snapshot.element.setAttribute('href', snapshot.previousHref);
  } else {
    snapshot.element.removeAttribute('href');
  }
  if (!document.head.contains(snapshot.element)) {
    document.head.appendChild(snapshot.element);
  }
}

/**
 * Sets document title and common SEO / Open Graph tags for CRA client routes.
 */
export function DocumentMeta({
  title,
  description,
  canonicalPath,
  ogType = 'website',
  robotsNoIndex = false,
  jsonLd = null,
}: DocumentMetaProps) {
  const canonicalUrl =
    canonicalPath && !robotsNoIndex
      ? `${SITE_ORIGIN}${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`
      : null;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;

    const snapshots: HeadTagSnapshot[] = [
      snapshotMeta('name', 'description', description),
      snapshotMeta('property', 'og:title', title),
      snapshotMeta('property', 'og:description', description),
      snapshotMeta('property', 'og:type', ogType),
      snapshotMeta('property', 'og:site_name', 'QInvesting'),
      snapshotMeta('property', 'og:image', DEFAULT_OG_IMAGE),
      snapshotMeta('name', 'twitter:card', 'summary'),
      snapshotMeta('name', 'twitter:title', title),
      snapshotMeta('name', 'twitter:description', description),
    ];

    let canonicalSnapshot: HeadTagSnapshot | null = null;
    let detachedCanonical: HeadTagSnapshot | null = null;
    let robotsSnapshot: HeadTagSnapshot | null = null;

    if (robotsNoIndex) {
      robotsSnapshot = snapshotMeta('name', 'robots', 'noindex,follow');
      detachedCanonical = detachCanonical();
    } else if (canonicalUrl) {
      canonicalSnapshot = snapshotCanonical(canonicalUrl);
      snapshots.push(snapshotMeta('property', 'og:url', canonicalUrl));
    } else {
      detachedCanonical = detachCanonical();
    }

    let jsonLdScript: HTMLScriptElement | null = null;
    if (jsonLd?.length) {
      jsonLdScript = mountHomeJsonLdScript(jsonLd);
    }

    return () => {
      jsonLdScript?.remove();
      document.title = previousTitle;
      if (robotsSnapshot) {
        restoreHeadTag(robotsSnapshot);
      }
      if (canonicalSnapshot) {
        restoreHeadTag(canonicalSnapshot);
      }
      restoreDetachedCanonical(detachedCanonical);
      snapshots.reverse().forEach(restoreHeadTag);
    };
  }, [title, description, canonicalUrl, ogType, robotsNoIndex, jsonLd]);

  return null;
}

export { SITE_ORIGIN };
